// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Landbridge — the bundled country dataset (Natural Earth 1:110m, public domain)
// plus the land-border ADJACENCY GRAPH that IS the game board. BFS over this graph
// gives par (the optimal route), the solvability guarantee, and the reveal of what
// everyone missed. Loaded at build time, fully offline.

import raw from './data/countries.json';
import type { LonLat } from './geo';

export type Pool = 'core' | 'extra' | 'territory';

export interface Country {
  id: string;
  name: string;
  cont: string;
  /** Centroid / label point [lon, lat]. */
  c: LonLat;
  pool: Pool;
  pop: number;
  /** Simplified exterior rings, each a flat [lon,lat,lon,lat,…] array. */
  rings: number[][];
  /** Ids of land-border neighbours (sorted; symmetric; empty for islands). */
  adj: string[];
  /** Connected-component index over the land-border graph. */
  comp: number;
}

interface RawData {
  source: string;
  sourceUrl: string;
  countries: Country[];
}

const DATA = raw as unknown as RawData;

export const SOURCE = DATA.source;
export const SOURCE_URL = DATA.sourceUrl;

export const ALL: readonly Country[] = DATA.countries;
export const BY_ID: ReadonlyMap<string, Country> = new Map(ALL.map((c) => [c.id, c]));

export function country(id: string): Country | undefined {
  return BY_ID.get(id);
}

/** Land-border neighbours of a country id (empty for an unknown id or an island). */
export function neighbors(id: string): readonly string[] {
  return BY_ID.get(id)?.adj ?? [];
}

export function borders(a: string, b: string): boolean {
  return neighbors(a).includes(b);
}

/** Component sizes, indexed by component id. */
const COMP_SIZE: number[] = (() => {
  const sizes: number[] = [];
  for (const c of ALL) sizes[c.comp] = (sizes[c.comp] ?? 0) + 1;
  return sizes;
})();

export function compSize(comp: number): number {
  return COMP_SIZE[comp] ?? 0;
}

/** Smallest landmass we treat as a routing "continent" for endpoints. */
export const MIN_ENDPOINT_COMP = 8;

/**
 * Countries eligible as a start/destination: a well-known ("core") country that
 * sits on a real landmass (a big connected component), so a route of several hops
 * always exists. Islands (empty comp) and tiny 2-node components are excluded.
 */
export const ENDPOINTS: readonly Country[] = ALL.filter(
  (c) => c.pool === 'core' && compSize(c.comp) >= MIN_ENDPOINT_COMP && c.adj.length > 0,
);

/** Full BFS distance map from a source over the border graph. */
export function bfsFrom(a: string): Map<string, number> {
  const dist = new Map<string, number>([[a, 0]]);
  let frontier = [a];
  let d = 0;
  while (frontier.length) {
    d++;
    const next: string[] = [];
    for (const u of frontier) {
      for (const v of neighbors(u)) {
        if (dist.has(v)) continue;
        dist.set(v, d);
        next.push(v);
      }
    }
    frontier = next;
  }
  return dist;
}

/** BFS hop-distance a→b over the border graph; Infinity if unreachable. */
export function bfsDist(a: string, b: string): number {
  if (a === b) return 0;
  const seen = new Set<string>([a]);
  let frontier = [a];
  let d = 0;
  while (frontier.length) {
    d++;
    const next: string[] = [];
    for (const u of frontier) {
      for (const v of neighbors(u)) {
        if (seen.has(v)) continue;
        if (v === b) return d;
        seen.add(v);
        next.push(v);
      }
    }
    frontier = next;
  }
  return Infinity;
}

/**
 * One shortest path a→b (inclusive of both ends), or [] if unreachable.
 * Deterministic: neighbours are pre-sorted and each node is discovered once, so
 * every peer computes the identical "optimal route" for the reveal.
 */
export function shortestPath(a: string, b: string): string[] {
  if (a === b) return [a];
  const prev = new Map<string, string>();
  const seen = new Set<string>([a]);
  let frontier = [a];
  let found = false;
  while (frontier.length && !found) {
    const next: string[] = [];
    for (const u of frontier) {
      for (const v of neighbors(u)) {
        if (seen.has(v)) continue;
        seen.add(v);
        prev.set(v, u);
        if (v === b) {
          found = true;
          break;
        }
        next.push(v);
      }
      if (found) break;
    }
    frontier = next;
  }
  if (!found) return [];
  const path = [b];
  let cur = b;
  while (cur !== a) {
    cur = prev.get(cur)!;
    path.push(cur);
  }
  return path.reverse();
}

/** Normalise a name for search / matching: lowercase, strip accents + punctuation. */
export function normalizeName(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, '')
    .trim();
}

/** Common alternative names people type, mapped to the canonical name. */
const ALIASES: Record<string, string> = {
  usa: 'United States',
  us: 'United States',
  america: 'United States',
  uk: 'United Kingdom',
  britain: 'United Kingdom',
  england: 'United Kingdom',
  uae: 'United Arab Emirates',
  drc: 'DR Congo',
  'ivory coast': "Côte d'Ivoire",
  burma: 'Myanmar',
  swaziland: 'Eswatini',
  'czech republic': 'Czechia',
  macedonia: 'North Macedonia',
  holland: 'Netherlands',
  'the netherlands': 'Netherlands',
};

const NORM_INDEX: Map<string, Country> = (() => {
  const m = new Map<string, Country>();
  for (const c of ALL) m.set(normalizeName(c.name), c);
  return m;
})();

/** Resolve a typed string to a country in `pool`, honouring aliases. Null if none. */
export function resolveTyped(input: string, pool: ReadonlySet<string>): Country | null {
  const n = normalizeName(input);
  if (!n) return null;
  const aliasTarget = ALIASES[n];
  const direct = aliasTarget ? NORM_INDEX.get(normalizeName(aliasTarget)) : NORM_INDEX.get(n);
  if (direct && pool.has(direct.id)) return direct;
  const hits = ALL.filter((c) => pool.has(c.id) && normalizeName(c.name).startsWith(n));
  return hits.length === 1 ? hits[0] : null;
}

/** Ranked search results within a pool: exact > alias > prefix > substring > word. */
export function searchCountries(input: string, pool: ReadonlySet<string>, limit = 8): Country[] {
  const n = normalizeName(input);
  if (!n) return [];
  const alias = ALIASES[n];
  const aliasId = alias ? NORM_INDEX.get(normalizeName(alias))?.id : undefined;
  const scored: Array<{ c: Country; s: number }> = [];
  for (const c of ALL) {
    if (!pool.has(c.id)) continue;
    const name = normalizeName(c.name);
    let s = Infinity;
    if (name === n) s = 0;
    else if (c.id === aliasId) s = 1;
    else if (name.startsWith(n)) s = 2;
    else if (name.includes(n)) s = 3;
    else if (n.length >= 3 && name.split(' ').some((w) => w.startsWith(n))) s = 4;
    if (s < Infinity) scored.push({ c, s });
  }
  scored.sort((a, b) => a.s - b.s || a.c.name.localeCompare(b.c.name));
  return scored.slice(0, limit).map((x) => x.c);
}
